import type { IBrand } from "@/props/IProduct";
import FilterTitle from "./FilterTitle";
import { useTranslation } from "next-i18next";
import { getBrandsApi } from "@/api/product";
import { useState, useEffect } from "react";
import { useRouter } from "next/router";
import Icon from "../Icon";
import useSWR from "swr";
import { onKeyActivate } from "@/utils/index";

interface BrandFilterProps {
  onChange: (brandId: string) => void;
  hideTitle?: boolean;
  badge?: number;
}

// fetcher 函数
const fetcher = async (): Promise<IBrand[]> => {
  const res: any = await getBrandsApi();
  return res;
};

const BrandFilter = ({ onChange, hideTitle = false, badge }: BrandFilterProps) => {
  const { t } = useTranslation();
  const router = useRouter();
  const currentLocale: string | undefined =
    router.locale?.toUpperCase() || "EN";

  // 使用 useSWR 请求品牌列表
  const { data: brandsData, error } = useSWR<IBrand[]>("brands", fetcher);

  const [brands, setBrands] = useState<IBrand[]>([]);
  const [showNum, setShowNum] = useState<number>(10);
  const [showMore, setShowMore] = useState<boolean>(false);

  const loading = !brandsData && !error;

  // 初始化 brands，加上 checked 属性
  useEffect(() => {
    if (!brandsData) return;
    const brandsWithChecked = brandsData.map((item) => ({
      ...item,
      checked: false,
    }));
    setBrands(brandsWithChecked);
  }, [brandsData]);

  // 更多/收起切换
  const handleShowMore = () => {
    setShowMore(!showMore);
    if (!showMore) {
      setShowNum(brands.length);
    } else {
      setShowNum(10);
    }
  };

  // 选择切换
  const handleSelect = (index: number) => {
    const data = JSON.parse(JSON.stringify(brands));
    data[index].checked = !data[index].checked;
    setBrands(data);

    const brandArr: number[] = [];
    data.forEach((item) => {
      if (item.checked) brandArr.push(item.id);
    });
    onChange(brandArr.join(","));
  };

  // Skeleton 占位加载组件
  const SkeletonLoader = () => (
    <div className="space-y-3">
      {[...Array(5)].map((_, i) => (
        <div key={i} className="flex items-center">
          <div className="mr-3 h-3 w-3 rounded bg-gray-200" />
          <div className="h-4 w-32 rounded bg-gray-200" />
        </div>
      ))}
    </div>
  );

  return (
    <div className="mb-8 flex w-full flex-col border-0 border-gray-100 lg:mb-0 lg:border-0 lg:px-0 lg:py-0">
      {!hideTitle && <FilterTitle title={t("product_category.brand_category")} badge={badge} />}
      <div className="flex flex-col">
        {loading ? (
          <SkeletonLoader />
        ) : (
          <div className="mb-2 flex flex-col">
            {brands.slice(0, showNum).map((brand, index) => (
              <div
                id={`checkicon-${brand?.nameEn?.replace(/\s+/g, "")}`}
                key={brand.id}
                className="mb-2 flex cursor-pointer items-center transition-colors hover:text-primary"
                onClick={() => handleSelect(index)}
                role="button"
                tabIndex={0}
                onKeyDown={onKeyActivate(() => handleSelect(index))}
              >
                {brand.checked ? (
                  <Icon
                    name="checked"
                    size={12}
                    title="icon check"
                    className="mr-3"
                  />
                ) : (
                  <Icon
                    name="check"
                    size={12}
                    title="icon check"
                    className="mr-3"
                  />
                )}

                <div className={`truncate ${brand.checked ? "text-stone-800" : "text-gray-400"}`}>
                  {currentLocale === "SC" ? brand.nameCn : brand.nameEn}
                </div>
              </div>
            ))}
          </div>
        )}
        {brands.length > 10 && (
          <div
            className="flex cursor-pointer items-center justify-center text-primary"
            onClick={handleShowMore}
          >
            {showMore
              ? t("product_category.expand")
              : t("product_category.more")}
            <Icon
              name="arrow-down"
              size={20}
              title="icon check"
              className={`ml-2 ${showMore ? "rotate-180" : ""}`}
            />
          </div>
        )}
      </div>
    </div>
  );
};

export default BrandFilter;
